// Reading a Tiled map into a `LevelSetup`.
//
// This is the only file that knows what a `.tmj` is. It knows about files,
// pixels and gids; it knows nothing about play. `src/engine/rules/campaign/` is the
// other half and has never heard of Tiled.
//
// **The one rule: tiles are kinds, objects are quantities.** A tile says what a
// square *is* — ground, a piece of some side and type, a belt part pointing
// somewhere. An object says *how much* — a heap of six, a promotion square
// with two uses. It is worth stating because the temptation, every time, is to
// add a tile for "heap of 3" and then another for "heap of 4".
//
// Every footgun below has bitten every project that has ever read one of these
// files, so each is handled explicitly and named.

import {
  type Direction,
  EAST,
  fileOf,
  forwardFacing,
  KING,
  makePiece,
  NORTH,
  PAWN,
  type PieceType,
  rankOf,
  SOUTH,
  square,
  type Side,
  WEST
} from "src/engine/rules/board";
import {
  KNOB_KEYS,
  type LevelKnobs,
  type LevelSetup,
  type ObjectiveKind,
  type ObjectiveSetup,
  type RegionSetup
} from "src/engine/rules/campaign/level";
import { STORE, TURRET } from "src/engine/rules/industry/state";
import { COAL, DEPOSIT_TIERS, depositFault, DIAMOND, ELECTRUM } from "src/engine/rules/materials";
import { FUEL, type MaterialLine, METAL, SPECIAL, STONE } from "src/shared/industry";
import { ZONE_KEY, ZONE_KINDS, zoneFault, type ZoneRow } from "src/engine/rules/zones";
import { RULES } from "src/engine/rules/rules";
// The one table the scenario picker also reads. A level's vocabulary and a
// player's are deliberately the same list.
import { ARMIES } from "src/engine/ai/scenarios";

/** The top three bits of a gid are flip flags, not part of the id. */
const GID_MASK = 0x1fffffff;

export interface TmjProperty {
  name: string;
  type?: string;
  value: unknown;
}

export interface TmjLayer {
  id?: number;
  name: string;
  type: "tilelayer" | "objectgroup" | "group";
  data?: number[];
  chunks?: unknown[];
  objects?: TmjObject[];
  layers?: TmjLayer[];
  properties?: TmjProperty[];
  width?: number;
  height?: number;
}

export interface TmjObject {
  id?: number;
  name?: string;
  class?: string;
  /** Tiled ≤1.8 wrote `type` where 1.9+ writes `class`. Both are read. */
  type?: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  gid?: number;
  point?: boolean;
  /** Degrees clockwise. A piece object's facing comes from this. */
  rotation?: number;
  properties?: TmjProperty[];
}

export interface TmjTileset {
  firstgid: number;
  /** Present when the tileset is external and has not been resolved yet. */
  source?: string;
  name?: string;
  tiles?: { id: number; properties?: TmjProperty[] }[];
}

export interface TmjMap {
  width: number;
  height: number;
  infinite?: boolean;
  layers: TmjLayer[];
  tilesets: TmjTileset[];
  properties?: TmjProperty[];
}

export class LevelParseError extends Error {}

const propertyOf = (properties: TmjProperty[] | undefined, name: string): unknown =>
  properties?.find((entry) => entry.name === name)?.value;

const stringProp = (properties: TmjProperty[] | undefined, name: string, fallback = ""): string => {
  const value = propertyOf(properties, name);
  return typeof value === "string" ? value : fallback;
};

const numberProp = (properties: TmjProperty[] | undefined, name: string, fallback: number): number => {
  const value = propertyOf(properties, name);
  return typeof value === "number" ? value : fallback;
};

const boolProp = (properties: TmjProperty[] | undefined, name: string, fallback = false): boolean => {
  const value = propertyOf(properties, name);
  return typeof value === "boolean" ? value : fallback;
};

const PIECE_TYPES: Record<string, PieceType> = {
  pawn: PAWN,
  knight: 2 as PieceType,
  bishop: 3 as PieceType,
  rook: 4 as PieceType,
  queen: 5 as PieceType,
  king: KING
};

const DIRECTIONS: Record<string, Direction> = {
  n: NORTH,
  e: EAST,
  s: SOUTH,
  w: WEST
};

/**
 * A piece object's facing, taken from the object's own rotation.
 *
 * **The orientation is in the file, where the author set it.** Tiled rotates
 * clockwise in degrees, and its canvas is y-down — so an unrotated object
 * points up the screen, which is up the board, which is north. Ninety degrees
 * clockwise from there points right, which is east. That is the whole mapping,
 * and it is the one an author gets by turning the thing in the editor and
 * looking at it.
 *
 * Snapped to the nearest quarter, because a facing is one of four things and an
 * author dragging a rotation handle will not land on 90.000. Negative and
 * past-360 rotations both normalise, since Tiled writes whatever the handle was
 * dragged to.
 */
const QUARTERS: Direction[] = [NORTH, EAST, SOUTH, WEST];

export function facingFromRotation(degrees: number): Direction {
  const quarter = Math.round(((degrees % 360) + 360) % 360 / 90) % 4;
  return QUARTERS[quarter];
}

/**
 * The properties of one tile, by gid.
 *
 * Built from the tilesets, and read by *property* rather than by arithmetic on
 * `gid - firstgid`. That subtraction is a position in a sheet, and the day
 * somebody inserts a row in the sheet every level in the repo changes army.
 * Properties survive that; the whole point of authoring in a tool is that the
 * tool's own metadata is the contract.
 */
type TileProperties = Map<number, TmjProperty[]>;

function tilePropertiesFrom(tilesets: TmjTileset[]): TileProperties {
  const out: TileProperties = new Map();
  for (const tileset of tilesets) {
    if (tileset.source) {
      throw new LevelParseError(
        `tileset "${tileset.source}" was not resolved — external tilesets must be fetched before parsing`
      );
    }
    for (const tile of tileset.tiles ?? []) {
      out.set(tileset.firstgid + tile.id, tile.properties ?? []);
    }
  }
  return out;
}

/**
 * Every object on the map, however deep in groups it is.
 *
 * For the things that are notes to the loader rather than contents of a square
 * — a `knobs` object is about the scenario, so making an author remember which
 * layer it belongs on would be a rule with nothing behind it.
 */
function everyObject(layers: TmjLayer[]): TmjObject[] {
  const out: TmjObject[] = [];
  for (const layer of layers) {
    if (layer.type === "objectgroup") out.push(...(layer.objects ?? []));
    if (layer.layers) out.push(...everyObject(layer.layers));
  }
  return out;
}

/** Layers of a kind, flattened out of any groups below this one. */
function layersOf(layers: TmjLayer[], type: TmjLayer["type"]): TmjLayer[] {
  return layers.filter((layer) => layer.type === type);
}

interface Frame {
  width: number;
  height: number;
  originFile: number;
  originRank: number;
}

/**
 * Tiled is y-down from the top-left and non-negative; a square is a packed
 * coordinate with rank running up. `originFile`/`originRank` default to zero,
 * which puts **a1 at the bottom-left of the Tiled canvas** — where a chess
 * player expects it.
 */
const squareAt = (frame: Frame, x: number, y: number): number =>
  square(x + frame.originFile, frame.height - 1 - y + frame.originRank);

/** Read one tile layer into `(square, gid)` pairs, skipping empty tiles. */
function* tilesOf(layer: TmjLayer, frame: Frame): Generator<[number, number]> {
  const data = layer.data;
  if (!data) return;
  const width = layer.width ?? frame.width;
  for (let index = 0; index < data.length; index++) {
    // `0` means no tile, and it is not an index into anything.
    const raw = data[index];
    if (raw === 0) continue;
    const gid = raw & GID_MASK;
    yield [squareAt(frame, index % width, Math.floor(index / width)), gid];
  }
}

/**
 * Where an object sits, in squares.
 *
 * **The object's centre, after rotation — not its anchor.** Tiled positions an
 * object by a corner and rotates it about that same corner, so a marker that
 * is merely turned in the editor moves off the square it was placed on. Which
 * corner also depends on the kind: a point *is* its point, a rectangle is its
 * top-left, a tile object its bottom-left.
 *
 * Flooring the anchor hid all of that from us and from nobody else. Tauric
 * Studio reads an object as `(x + w/2 - tile/2, y + h)` and undoes the
 * rotation about the corner to recover a centre
 * (`src/formats/tiled/tmjLoader.ts`), which is the general reading — so a
 * zero-size point written at a square's centre came out **half a tile high**
 * in that editor, on every marker in the level, and authoring was a
 * non-starter. Reported from trying to open the map.
 *
 * So this computes the same centre. Doing it here rather than asking authors
 * for a convention is the point: the file is then whatever a general editor
 * round-trips, and both readings agree by construction.
 */
function objectSquare(object: TmjObject, frame: Frame, tileSize: number): number {
  // A point has no extent, so its own coordinates are already its centre. A
  // tile object hangs above its anchor; everything else grows down and right.
  const width = object.point ? 0 : object.width ?? tileSize;
  const height = object.point ? 0 : object.height ?? tileSize;
  const top = object.gid ? object.y - height : object.y;

  let cx = object.x + width / 2;
  let cy = top + height / 2;
  if (object.rotation) {
    // About the anchor, which is what Tiled does and what an author sees.
    const radians = (object.rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    cx = object.x + (width / 2) * cos - (height / 2) * sin;
    cy = top + (width / 2) * sin + (height / 2) * cos;
  }

  return squareAt(frame, Math.floor(cx / tileSize), Math.floor(cy / tileSize));
}

const classOf = (object: TmjObject): string => object.class ?? object.type ?? "";

/**
 * A `zone` object as a zone row (§31 D3): its rectangle in whole tiles,
 * south-west corner first. Refused, like every other authoring slip, for an
 * unknown kind or a rectangle `zoneFault` would refuse.
 */
function zoneRowOf(object: TmjObject, frame: Frame, tileSize: number, room: string): ZoneRow {
  const name = stringProp(object.properties, "kind", "");
  const kind = ZONE_KINDS.find((k) => ZONE_KEY[k] === name);
  if (kind === undefined) {
    throw new LevelParseError(
      `unknown zone "${name}" in "${room}" — one of ${ZONE_KINDS.map((k) => ZONE_KEY[k]).join(", ")}`
    );
  }
  const width = Math.round((object.width ?? tileSize) / tileSize);
  const height = Math.round((object.height ?? tileSize) / tileSize);
  const corner = squareAt(frame, Math.floor(object.x / tileSize), Math.floor(object.y / tileSize) + height - 1);
  const layout = numberProp(object.properties, "layout", -1);
  const row: ZoneRow =
    layout >= 0
      ? [kind, fileOf(corner), rankOf(corner), width, height, layout]
      : [kind, fileOf(corner), rankOf(corner), width, height];
  const fault = zoneFault(row);
  if (fault) throw new LevelParseError(`zone in "${room}": ${fault}`);
  return row;
}

/**
 * A level is a set of **rooms**, and a room is two layers.
 *
 * The group's *name is the room's id*, and it is a number: there is nothing for
 * a name to say that its contents do not, and a prefix convention
 * (`region:start`) is a thing to misspell in an editor that offers no
 * completion. Numbers also give the one ordering a level actually has.
 */
const GROUND_LAYER = "ground";
const PIECES_LAYER = "pieces";

/**
 * Which side a tile layer fields, read off its name.
 *
 * A room's tile layers are `ground` and `pieces`, and a `pieces` layer may
 * name a side: `pieces 1` fields side 1, bare `pieces` (and `ground`) side 0.
 * **The name, because it is what an editor round-trips.** The chess tileset
 * draws one army, so a piece tile's side has to come from outside the tile —
 * and Tauric keeps a tile layer's name, hierarchy and flip bits and nothing
 * else about it, so a `side` property on the layer would be lost on the way
 * through. A layer per side is also how an author sees whose pieces are whose
 * in the layer list. Anything else is a typo, and faults: the failure mode of
 * reading whatever is there is that a misspelled layer silently contributes
 * nothing, which looks exactly like a layer that did not matter.
 */
const PIECES_TILE_LAYER = /^pieces(?: (\d+))?$/;
/**
 * Where the arrows go, by convention: an arrow laid on `ground` displaces the
 * floor tile under it, so it wants a layer of its own. Fields side 0 like
 * `ground` if a piece is ever laid on it.
 */
const ARROWS_LAYER = "arrows";

function tileLayerSide(layer: TmjLayer, room: string): Side {
  if (layer.name === GROUND_LAYER || layer.name === ARROWS_LAYER) return 0 as Side;
  const match = PIECES_TILE_LAYER.exec(layer.name);
  if (match) return Number(match[1] ?? 0) as Side;
  throw new LevelParseError(
    `room "${room}" has a tilelayer called "${layer.name}" — a room's tile layers are ` +
      `"${GROUND_LAYER}", "${ARROWS_LAYER}" and "${PIECES_LAYER}" (or "${PIECES_LAYER} <side>"), ` +
      `and its object layers "${PIECES_LAYER}"`
  );
}

/**
 * What the tile layers say that is not a room's own content: the pawns laid
 * as tiles, which have no facing of their own yet, and the arrow tiles that
 * give them one. Level-wide, because an arrow is found by distance and the
 * author draws every room on one canvas.
 */
interface TileNotes {
  tilePawns: [region: RegionSetup, square: number, side: Side][];
  arrows: [square: number, direction: Direction][];
}

/** A `deposit` marker's `material`, as line and grade. */
const DEPOSIT_MATERIALS: Record<string, [MaterialLine, number]> = {
  iron: [METAL, 0],
  cobble: [STONE, 0],
  coal: [FUEL, COAL],
  electrum: [SPECIAL, ELECTRUM],
  diamond: [SPECIAL, DIAMOND]
};

/** Everything that may stand on a room's floor, beside the pieces themselves. */
const ROOM_CLASSES = new Set([
  "heap", "metal", "coal", "deposit", "promotion", "facing", "connect", "knobs", "belt", "army",
  // A zone the room grows (§31 D3).
  "zone",
  // Named by kind, for the reason a piece is named by rank: `knight` rather
  // than `piece` with a property behind a panel. An author picks the thing,
  // not the thing plus a setting.
  "store", "turret",
  // Not room content — a note about what the room is worth as a camp.
  "camp"
]);

/** Is this group a room, rather than something an author parked beside one? */
const roomNumber = (name: string): number => Number(name.trim());

function readRegion(
  group: TmjLayer,
  frame: Frame,
  tileSize: number,
  tiles: TileProperties,
  notes: TileNotes
): RegionSetup {
  const id = group.name.trim();
  const region: RegionSetup = {
    id,
    // **Derived, never authored.** Which objective opens a room is a fact
    // about the objective — it is the thing that carries `reveals`, and it is
    // an *object*, which is what an editor can put a properties panel on. A
    // room property saying the same thing is a second copy to disagree with,
    // and it disagreed silently.
    revealedBy: "",
    gambit: false,
    cells: [],
    pieces: [],
    belts: [],
    heaps: [],
    metal: [],
    promotion: [],
    facing: []
  };

  const children = group.layers ?? [];
  // **Named layers.** A room is ground, the pieces standing on it, and the
  // markers beside them, so anything else in the group is an author's typo.
  // Tile layers are checked as they are read (`tileLayerSide`); an object
  // layer is `pieces`.
  for (const layer of layersOf(children, "objectgroup")) {
    if (layer.name !== PIECES_LAYER) {
      throw new LevelParseError(
        `room "${group.name}" has an objectgroup called "${layer.name}" — a room's ` +
          `object layers are "${PIECES_LAYER}"`
      );
    }
  }
  for (const layer of layersOf(children, "tilelayer")) {
    const layerSide = tileLayerSide(layer, group.name);
    for (const [at, gid] of tilesOf(layer, frame)) {
      const properties = tiles.get(gid);
      if (!properties) {
        throw new LevelParseError(`tile ${gid} in "${group.name}/${layer.name}" is in no tileset`);
      }

      const kind = stringProp(properties, "kind");
      if (kind) {
        region.cells.push(at);
        continue;
      }

      const type = stringProp(properties, "type");
      if (type) {
        const pieceType = PIECE_TYPES[type];
        if (pieceType === undefined) {
          throw new LevelParseError(`unknown piece type "${type}" in "${group.name}"`);
        }
        // The tile's own side where the tileset states one, else the layer's.
        const side = numberProp(properties, "side", layerSide) as Side;
        region.pieces.push([at, makePiece(side, pieceType)]);
        if (pieceType === PAWN) notes.tilePawns.push([region, at, side]);
        continue;
      }

      const arrow = stringProp(properties, "arrow");
      if (arrow) {
        const direction = DIRECTIONS[arrow];
        if (direction === undefined) {
          throw new LevelParseError(`unknown arrow "${arrow}" in "${group.name}"`);
        }
        notes.arrows.push([at, direction]);
        continue;
      }

      const dir = stringProp(properties, "dir");
      if (dir) {
        const direction = DIRECTIONS[dir];
        if (direction === undefined) {
          throw new LevelParseError(`unknown belt direction "${dir}" in "${group.name}"`);
        }
        region.belts.push([at, direction, numberProp(properties, "owner", 0) as Side]);
        continue;
      }

      throw new LevelParseError(
        `tile ${gid} in "${group.name}/${layer.name}" has no kind, type, dir or arrow property`
      );
    }
  }

  for (const layer of layersOf(children, "objectgroup")) {
    for (const object of layer.objects ?? []) {
      const at = objectSquare(object, frame, tileSize);
      const kind = classOf(object);

      // **A piece may be an object as well as a tile**, and as an object it
      // carries its own orientation. A tile can only say *what* is on a square:
      // Tiled's tile flips are three bits with no room for four facings, so a
      // tile-laid pawn needed a second `facing` marker beside it to say which
      // way it looked — two objects for one piece, and nothing stopping them
      // drifting apart. An object says both at once, and an author turns it in
      // the editor and sees the answer.
      //
      // The class names the rank, because that is what reads in a Tiled object
      // list and in Tauric Studio's inspector: `knight`, not `piece` with a
      // property you have to open the panel to see.
      const asPiece = PIECE_TYPES[kind];
      if (asPiece !== undefined) {
        const side = numberProp(object.properties, "side", 0) as Side;
        region.pieces.push([at, makePiece(side, asPiece)]);
        // Only when it was actually turned. An unrotated piece keeps the
        // side's forward facing, which is what every ordinary army wants and
        // what `openRegion` already does — writing north here would point
        // Black's pawns backwards on every map that never touched a handle.
        if (object.rotation) {
          region.facing.push([at, facingFromRotation(object.rotation)]);
        }
        continue;
      }

      switch (kind) {
        case "heap":
          region.heaps.push([at, numberProp(object.properties, "amount", 1)]);
          break;
        case "metal":
          // Refined material on the floor. A heap cannot say this — scrap is
          // grade-0 by definition — so a level that opens the player on silver
          // needs a marker of its own.
          (region.metal ??= []).push([
            at,
            numberProp(object.properties, "grade", 1),
            numberProp(object.properties, "count", 1)
          ]);
          break;
        case "coal":
          // Coal on the floor (§31 M2), what the room's metal is raised with.
          (region.coal ??= []).push([at, numberProp(object.properties, "count", 1)]);
          break;
        case "deposit": {
          // Ore in the ground, authored (§31 D1's tile): `material` by name,
          // `tier` a mixed tile's word ("thin", "concentrated", "rich"; none
          // is pure), `amount` defaulting to a zone tile's.
          const name = stringProp(object.properties, "material", "");
          const material = DEPOSIT_MATERIALS[name];
          const tierWord = stringProp(object.properties, "tier", "");
          const tier = DEPOSIT_TIERS.findIndex((entry) => entry.word === tierWord);
          if (!material) {
            throw new LevelParseError(
              `unknown deposit "${name}" in "${group.name}" — one of ${Object.keys(DEPOSIT_MATERIALS).join(", ")}`
            );
          }
          const fault = tier < 0 ? `unknown tier "${tierWord}"` : depositFault(material[0], material[1], tier);
          if (fault) throw new LevelParseError(`deposit in "${group.name}": ${fault}`);
          (region.deposits ??= []).push([
            at,
            material[0],
            material[1],
            numberProp(object.properties, "amount", RULES.depositAmount),
            tier
          ]);
          break;
        }
        case "promotion":
          region.promotion.push([at, numberProp(object.properties, "uses", 2)]);
          break;
        case "facing": {
          const dir = DIRECTIONS[stringProp(object.properties, "dir", "n")];
          if (dir === undefined) {
            throw new LevelParseError(`unknown facing in "${group.name}"`);
          }
          region.facing.push([at, dir]);
          break;
        }
        case "army": {
          // **Which faction a side in this room fields**, as the id the
          // scenario screen picks (`ARMIES`) — one vocabulary, or a faction
          // becomes authorable and unpickable. Only "factory" since §31 R2.
          const id = stringProp(object.properties, "kind", "");
          const army = ARMIES.find((entry) => entry.id === id);
          if (!army) {
            throw new LevelParseError(
              `unknown army "${id}" in "${group.name}" — see ARMIES (${ARMIES.map((a) => a.id).join(", ")})`
            );
          }
          const side = numberProp(object.properties, "side", 1) as Side;
          (region.kings ??= []).push([side, army.kind]);
          break;
        }
        case "store":
        case "turret": {
          // **A building takes its square off the board**, which `openRegion`
          // does by filtering the ground it lays — so this is placed and the
          // cell is dropped in the same breath, exactly as `build` does it.
          (region.structures ??= []).push([
            at,
            kind === "turret" ? TURRET : STORE,
            numberProp(object.properties, "side", 1) as Side
          ]);
          break;
        }
        case "zone":
          // A rectangle the room grows as a zone (§31 D3) — the map-makers'
          // door. `kind` is the zone's wire name ("quarry", "broken-lands");
          // `layout` an island layout for Broken Lands. Contents off the seed.
          (region.zones ??= []).push(zoneRowOf(object, frame, tileSize, group.name));
          break;
        case "camp":
          // What the room is worth *as a camp*, read where an author can see
          // it. Parked in the room like `knobs` and for the same reason: it is
          // a note to the loader rather than something standing on a square.
          region.camp = {
            weight: numberProp(object.properties, "weight", 1),
            minStage: numberProp(object.properties, "minStage", 0) || undefined,
            minPlayerPieces: numberProp(object.properties, "minPlayerPieces", 0) || undefined,
            maxPlayerPieces: numberProp(object.properties, "maxPlayerPieces", 0) || undefined
          };
          break;
        case "knobs":
          // Not region content. It is a note to the loader that happens to be
          // parked here, and an author should be able to put it beside the
          // thing it tunes rather than on one blessed layer.
          break;
        case "connect":
          // Where this region joins what is already on the board. One per
          // region: a second is an author describing two bridges and getting
          // whichever the file happens to list last, which is the sort of thing
          // that is only ever discovered from a level that looks wrong.
          if (region.connect !== undefined) {
            throw new LevelParseError(`region "${group.name}" has more than one connect marker`);
          }
          region.connect = at;
          break;
        default:
          // **Objectives share the layer and are read separately.** They stand
          // on a room's floor because that is what they are about — but what
          // they mean is a sequence across the whole level, so `readObjectives`
          // sweeps for them rather than each room collecting its own.
          if (OBJECTIVE_KINDS.has(kind)) break;
          throw new LevelParseError(
            `marker of class "${kind}" in room "${group.name}" is not an objective, ` +
              `a piece, heap, metal, deposit, promotion, facing, connect or knobs`
          );
      }
    }
  }

  // Sorted, always. Two clients — or a game and its replay — walk these in the
  // order they were written, and Tiled's order is an authoring accident.
  region.cells.sort((a, b) => a - b);
  region.pieces.sort(([a], [b]) => a - b);
  region.belts.sort(([a], [b]) => a - b);
  region.heaps.sort(([a], [b]) => a - b);
  region.metal?.sort(([a], [b]) => a - b);
  region.promotion.sort(([a], [b]) => a - b);
  region.facing.sort(([a], [b]) => a - b);
  return region;
}

/**
 * A pawn laid as a tile faces the nearest arrow tile, or north.
 *
 * A tile cannot carry a facing — Tiled's flip bits are three and a facing is
 * one of four — so the chess tileset carries four arrows instead, and a pawn
 * laid as a tile looks the way the nearest one points. One arrow behind an
 * army aims the whole army, which is the case that matters. Measured across
 * the whole level rather than the room, since the author draws every room on
 * one canvas and sees one distance; ties go to the arrow on the lower square,
 * so two clients read one file the same way. No arrow anywhere and it faces
 * north, whichever side it is — the stated default, and unlike an object
 * pawn, which keeps its side's forward and carries its own rotation. An
 * explicit `facing` marker on the square still wins.
 *
 * **A facing equal to the side's forward is not written.** `openRegion` lands
 * every pawn facing forward, so an arrow saying the same overrides nothing —
 * and leaving it out is what lets a level ported from rotated objects to
 * tiles, with an arrow under every pawn, parse exactly as it did.
 *
 * Pawns only, because facing is a pawn's rule: the rest of an army turns to
 * look the way its pawns do (`armyFacing`), so aiming the pawns aims it.
 */
function aimTilePawns({ tilePawns, arrows }: TileNotes): void {
  const aimed = new Set<RegionSetup>();
  for (const [region, at, side] of tilePawns) {
    if (region.facing.some(([sq]) => sq === at)) continue;
    let best: Direction = NORTH;
    let bestDistance = Infinity;
    let bestSquare = Infinity;
    for (const [sq, direction] of arrows) {
      const df = fileOf(sq) - fileOf(at);
      const dr = rankOf(sq) - rankOf(at);
      const distance = df * df + dr * dr;
      if (distance < bestDistance || (distance === bestDistance && sq < bestSquare)) {
        best = direction;
        bestDistance = distance;
        bestSquare = sq;
      }
    }
    if (best === forwardFacing(side)) continue;
    region.facing.push([at, best]);
    aimed.add(region);
  }
  // Sorted again, since these landed after the room's own sort.
  for (const region of aimed) region.facing.sort(([a], [b]) => a - b);
}

/**
 * What a `deliver` objective asks for when the author does not say.
 *
 * An ordinary delivery is one unit of anything — the smallest thing an author
 * can mean. A **pad** is the rocket (§12.4.9), so its default is
 * `RULES.rocketCost`, and the level file then says only *that there is a pad
 * here* rather than restating a number that lives in `rules.ts`.
 */
const padDefaults = (object: TmjObject): { count: number; grade: number } =>
  boolProp(object.properties, "deliverTo")
    ? { count: RULES.rocketCost.units, grade: RULES.rocketCost.grade }
    : { count: 1, grade: 0 };

/**
 * The scenario's constant overrides, off a single `knobs` object.
 *
 * **An object rather than map properties**, even though a map already carries
 * properties and `levelName` lives there. Two reasons, and the second is the
 * one that matters: a `knobs` object is *positioned*, so an author can park it
 * next to the thing it tunes and see it on the canvas; and map properties are a
 * flat namespace shared with `levelId`, `originFile`, `final` and everything
 * added later, so a knob named like one of those would silently fight it.
 *
 * Every property must name a numeric key of `RULES`. A knob naming something
 * that does not exist is a **fault**, not a no-op — the failure mode of a
 * settings bag is that a typo looks exactly like a setting which did not
 * matter, and from inside Tiled there is no way to tell those apart.
 */
function readKnobs(object: TmjObject): LevelKnobs {
  const knobs: LevelKnobs = {};
  const allowed = new Set(KNOB_KEYS);
  for (const property of object.properties ?? []) {
    if (!allowed.has(property.name)) {
      throw new LevelParseError(
        `knob "${property.name}" is not a tunable — see RULES in src/engine/rules/rules.ts`
      );
    }
    if (typeof property.value !== "number") {
      throw new LevelParseError(
        `knob "${property.name}" is ${typeof property.value}, and every knob is a number`
      );
    }
    knobs[property.name] = property.value;
  }
  return knobs;
}

const OBJECTIVE_KINDS = new Set<string>(["defeat", "deliver", "reach", "collect"]);

function readObjectives(
  objects: readonly TmjObject[],
  frame: Frame,
  tileSize: number
): ObjectiveSetup[] {
  return objects.map((object) => {
    const kind = classOf(object);
    const reveals = stringProp(object.properties, "reveals")
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);

    return {
      id: object.name ?? "",
      order: numberProp(object.properties, "order", 1),
      kind: kind as ObjectiveKind,
      side: numberProp(object.properties, "side", 1) as Side,
      // **A pad's default cost is the rocket's** (`RULES.rocketCost`), which is
      // what makes that tunable a tunable at all: it was defined, documented
      // and referenced by nothing, so a level would have had to write 36 and
      // 3 into a `.tmj` and the number in `rules.ts` would drift the first
      // time anybody swept it. §12.4.9 asks for it to be denominated in units
      // and a grade *in the one place tunables live*; that only means
      // something if the level reads it.
      //
      // A default, never an override — an author who states a count gets that
      // count, so a pad can still be authored for something other than a
      // finale.
      count: numberProp(object.properties, "count", padDefaults(object).count),
      grade: numberProp(object.properties, "grade", padDefaults(object).grade),
      square: objectSquare(object, frame, tileSize),
      // **The rocket pad (§12.4.9), and it was unreachable from a file.**
      // `ObjectiveSetup.deliverTo` is honoured by `objectives.ts`, exempted
      // from the king's-hands bound by `validateLevel`, and covered by its own
      // tests — and nothing read it here, so no authored level could ever ask
      // for a delivery to anywhere but the player's king. That is the same
      // hole `KING_VON_NEUMANN` shipped through: rules, a profile and a goal
      // with no way to put one on a board.
      //
      // **A flag rather than the square's presence**, which is the reason it
      // needs reading at all: every objective carries a square, because the
      // loader takes it from the Tiled marker's own position, so "has a
      // square" is true of all of them and cannot mean "deliver here".
      deliverTo: boolProp(object.properties, "deliverTo"),
      reveals,
      // **On the objective, because that is when a Gambit fires.** It used to
      // be a property of the *group*, and a group is a layer — the one thing
      // in a Tiled file an editor is least likely to give you a properties
      // panel for. Ground opening is an objective completing, so the switch
      // belongs on the object that opens it.
      gambit: boolProp(object.properties, "gambit"),
      text: stringProp(object.properties, "text", object.name ?? "")
    };
  });
}

/**
 * Turn an already-fetched map and its resolved tilesets into a level.
 *
 * Pure, so it is testable without a network or a filesystem — which matters,
 * because the interesting cases are all malformed maps.
 */
export function parseLevel(map: TmjMap, tileSize = 32): LevelSetup {
  // Infinite maps store `chunks` rather than `data`, and would let an author
  // use negative coordinates directly. Rejected rather than silently read as
  // an empty layer, which is the failure mode if nobody writes this check.
  if (map.infinite) {
    throw new LevelParseError(
      "infinite maps are not supported — set the map to a fixed size in Tiled"
    );
  }

  const frame: Frame = {
    width: map.width,
    height: map.height,
    originFile: numberProp(map.properties, "originFile", 0),
    originRank: numberProp(map.properties, "originRank", 0)
  };
  const tiles = tilePropertiesFrom(map.tilesets);

  const groups = layersOf(map.layers, "group");
  for (const group of groups) {
    if (!Number.isFinite(roomNumber(group.name))) {
      throw new LevelParseError(
        `group "${group.name}" is not a room — a level's groups are its rooms, ` +
          `named by number`
      );
    }
  }
  const notes: TileNotes = { tilePawns: [], arrows: [] };
  const regions = groups.map((group) => readRegion(group, frame, tileSize, tiles, notes));
  aimTilePawns(notes);

  // **Objectives live in the room they are about**, on the same `pieces` layer
  // as everything else standing on that ground. They used to have a layer of
  // their own at the top of the map, which put the one marker that has to
  // stand on a room's floor (`objective-adrift`) as far from that floor as the
  // file allows — and it was how the shipped level's markers ended up on a
  // canvas corner nobody had paved.
  // **Every object is something, wherever it is.** Objectives are found by
  // class rather than by which layer they sit on, so a misspelled one would
  // otherwise be quietly not-an-objective — and a typo that looks exactly like
  // a marker that did not matter is the failure mode this loader faults on
  // everywhere else. A room's own switch catches the ones inside a room; this
  // catches the rest.
  for (const object of everyObject(map.layers)) {
    const kind = classOf(object);
    if (!OBJECTIVE_KINDS.has(kind) && !ROOM_CLASSES.has(kind) && PIECE_TYPES[kind] === undefined) {
      throw new LevelParseError(
        `object of class "${kind}" is not an objective, a piece, or any marker ` +
          `this loader knows`
      );
    }
  }
  const objectives = readObjectives(
    everyObject(map.layers).filter((object) => OBJECTIVE_KINDS.has(classOf(object))),
    frame,
    tileSize
  );

  // Which objective opens which room, folded back the one way round. A room
  // nothing names is on the board from the start.
  for (const objective of objectives) {
    for (const id of objective.reveals) {
      const room = regions.find((region) => region.id === id);
      if (!room) {
        throw new LevelParseError(
          `objective "${objective.id}" reveals room "${id}", which is not in this level`
        );
      }
      room.revealedBy = objective.id;
      room.gambit = objective.gambit ?? false;
    }
  }

  // Anywhere on the map, on any object layer — it is a note to the loader, not
  // a thing on a square, so making an author remember which layer it lives on
  // would be a rule with nothing behind it.
  const knobObjects = everyObject(map.layers).filter(
    (object) => classOf(object) === "knobs"
  );
  if (knobObjects.length > 1) {
    throw new LevelParseError("more than one knobs object — a scenario tunes itself once");
  }
  const knobs = knobObjects[0] ? readKnobs(knobObjects[0]) : undefined;

  if (regions.length === 0) {
    throw new LevelParseError("no rooms — a level is one or more groups named by number");
  }

  return {
    levelId: stringProp(map.properties, "levelId", "untitled"),
    levelName: stringProp(map.properties, "levelName", "Untitled"),
    playerSide: numberProp(map.properties, "playerSide", 0) as Side,
    // Layer order is not region order: regions open in the order their
    // objectives complete, never in the order Tiled happens to list them.
    // A map property, so whether a scenario ends the run is a thing the level
    // author sets in Tiled beside its name rather than a list kept in code.
    final: boolProp(map.properties, "final"),
    ...(knobs && Object.keys(knobs).length > 0 ? { knobs } : {}),
    // **Numerically**, or room 10 sorts between 1 and 2. Layer order is not
    // room order either: rooms open in the order their objectives complete,
    // never in the order Tiled happens to list them.
    regions: regions.sort((a, b) => roomNumber(a.id) - roomNumber(b.id)),
    objectives: objectives.sort((a, b) => a.order - b.order)
  };
}
